import React from "react";
import { getLayout as getDefaultLayout } from "components/layouts/default/Default";
import { useRouter } from "next/router";
import produce from "immer";
import { queryCache, useMutation } from "react-query";
import styled, { css } from "styled-components";
import moment from "moment";
import {
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Popconfirm,
  Progress,
  Tooltip,
} from "antd";
import {
  EditOutlined,
  CheckCircleTwoTone,
  LoadingOutlined,
} from "@ant-design/icons";
import useJobDetailData from "hooks/api/job/useJobDetailData";
import CustomLink from "components/shared/CustomLink";
import * as ROUTES from "constants/routes";
import JobInfoSkeleton, { TagSkeleton } from "lib/loader/job/jobInfoSkeleton";
import { NotificationStateContext } from "context/UI/Notification/NotificationState";
import { apiAxios } from "services/HttpServices";

const { Text, Title, Link } = Typography;

const approve = async ({ type, jobId, approvalId }) => {
  try {
    const res = await apiAxios.post(
      `/jobs/${jobId}/approval/${approvalId}/${type}`
    );
    return res.data.data;
  } catch (e) {
    throw e.response.data.message;
  }
};

const updateJobStatus = async ({ jobId, status }) => {
  try {
    const res = await apiAxios.put(`/jobs/${jobId}/status`, { status: status });
    return res.data.data;
  } catch (e) {
    throw e.response.data.message;
  }
};

const JobLayout = (props) => {
  const router = useRouter();
  const { id } = router.query;
  const { data, status } = useJobDetailData(id);
  const {
    state: notificationState,
    setSuccessMessage,
    clearSuccessMessage,
    setLoading,
    finishLoading,
  } = React.useContext(NotificationStateContext);

  const [approvalError, setApprovalError] = React.useState({
    order: null,
    ctp: null,
  });

  const [updateJobStatusError, setUpdateJobStatusError] = React.useState(null);

  //Mutation
  const [mutateApproval] = useMutation(approve, {
    onSuccess: (newData, variables) => {
      queryCache.setQueryData(
        ["job-detail", variables.jobId.toString()],
        function (oldData) {
          const updatedData = produce(oldData, (draft) => {
            draft.approval = newData;
          });
          return updatedData;
        }
      );
    },
    onError: (e, variables) => {
      setApprovalError(
        produce((draft) => {
          draft[variables.type] = e;
        })
      );
    },
  });

  const [mutateJobStatus] = useMutation(updateJobStatus, {
    onSuccess: (newData, variables) => {
      queryCache.setQueryData(
        ["job-detail", variables.jobId.toString()],
        function (oldData) {
          const updatedData = produce(oldData, (draft) => {
            draft.status = newData.status;
            draft.start_date = newData.start_date;
            draft.end_date = newData.end_date;
          });
          return updatedData;
        }
      );
    },
    onError: (e) => {
      setUpdateJobStatusError(e.response.data);
    },
  });

  const handleApproveJobOrder = async (approvalId) => {
    setApprovalError(approvalError);
    setLoading("approveJobOrder");
    try {
      await mutateApproval({ type: "order", jobId: id, approvalId });
      finishLoading("approveJobOrder");
    } catch (error) {
      finishLoading("approveJobOrder");
    }
  };

  const handleApproveCtp = async (approvalId) => {
    setApprovalError(approvalError);
    setLoading("approveCTP");
    try {
      await mutateApproval({ type: "ctp", jobId: id, approvalId });
      finishLoading("approveCTP");
    } catch (error) {
      finishLoading("approveCTP");
    }
  };

  const handleUpdateJobStatus = async (status) => {
    setUpdateJobStatusError(null);
    setLoading("updateJobStatus");
    try {
      await mutateJobStatus({ jobId: id, status });
      finishLoading("updateJobStatus");
    } catch (error) {
      finishLoading("updateJobStatus");
    }
  };

  const datelineCompleted = data?.dateline?.filter(
    (dateline) => dateline.completed
  );
  const completionPercentage = Math.round(
    ((datelineCompleted?.length / data?.dateline?.length) * 100).toFixed(2)
  );

  return (
    <>
      {status !== "loading" ? (
        <>
          {data && (
            <>
              {data.approval.order_by === null && (
                <StyledNotifRow gutter={4}>
                  <Col span={24}>
                    <div className="container">
                      <p>This job order is still waiting for approval</p>
                      <Popconfirm
                        title="Are you sure approved this job order?"
                        onConfirm={() =>
                          handleApproveJobOrder(data.approval.id)
                        }
                        onCancel={() => {
                          return;
                        }}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button
                          loading={notificationState.loading.approveJobOrder}
                        >
                          Approve job order
                        </Button>
                      </Popconfirm>
                    </div>
                    {approvalError["order"] && (
                      <StyledErrorMessage>
                        <p>Error: {approvalError.order}</p>
                      </StyledErrorMessage>
                    )}
                  </Col>
                </StyledNotifRow>
              )}

              {data.approval.ctp_by === null && (
                <StyledNotifRow gutter={4}>
                  <Col span={24}>
                    <div className="container">
                      <p>Waiting for CTP Dept. to approved this job details</p>
                      <Popconfirm
                        title="Are you sure approved this job details?"
                        onConfirm={() => handleApproveCtp(data.approval.id)}
                        onCancel={() => {
                          return;
                        }}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button loading={notificationState.loading.approveCTP}>
                          Approve job details
                        </Button>
                      </Popconfirm>
                    </div>
                    {approvalError["ctp"] && (
                      <StyledErrorMessage>
                        <p>Error: {approvalError.ctp}</p>
                      </StyledErrorMessage>
                    )}
                  </Col>
                </StyledNotifRow>
              )}

              <StyledHeaderRow
                urgent={data?.job_general?.urgent ? "true" : "false"}
                gutter={4}
                justify="middle"
              >
                <>
                  <Col span={16} style={{ paddingRight: "10px" }}>
                    <Row>
                      <Col span={16}>
                        <StyledText
                          $companyName
                          urgent={data?.job_general?.urgent ? "true" : "false"}
                        >
                          {data?.client?.name || "-"}
                        </StyledText>
                        <StyledTitle
                          level={3}
                          $jobName
                          urgent={data?.job_general?.urgent ? "true" : "false"}
                        >
                          {data?.name}
                        </StyledTitle>
                      </Col>
                      <Col span={8}>
                        <CreatedAtContainer>
                          <StyledText
                            $createdAt
                            urgent={
                              data?.job_general?.urgent ? "true" : "false"
                            }
                          >
                            Created At
                          </StyledText>
                          <StyledText
                            $createdAt
                            urgent={
                              data?.job_general?.urgent ? "true" : "false"
                            }
                          >
                            {moment(data?.created_at).format("DD MMM YYYY")}
                          </StyledText>
                        </CreatedAtContainer>
                      </Col>
                    </Row>
                    <StyledRow justify="middle">
                      <Col span={8}>
                        <DateContainer>
                          <StyledText
                            $jobDateLabel
                            urgent={
                              data?.job_general?.urgent ? "true" : "false"
                            }
                          >
                            Start At
                          </StyledText>
                          <StyledText
                            $createdAt
                            urgent={
                              data?.job_general?.urgent ? "true" : "false"
                            }
                          >
                            {data?.start_date
                              ? moment(data.start_date).format("DD MMM YY")
                              : "-"}
                          </StyledText>
                        </DateContainer>
                      </Col>

                      <Col span={8}>
                        <DateContainer>
                          <StyledText
                            $jobDateLabel
                            urgent={
                              data?.job_general?.urgent ? "true" : "false"
                            }
                          >
                            Completed at
                          </StyledText>
                          <StyledText
                            $createdAt
                            urgent={
                              data?.job_general?.urgent ? "true" : "false"
                            }
                          >
                            {data?.end_date
                              ? moment(data?.end_date).format("DD MMM YY")
                              : "-"}
                          </StyledText>
                        </DateContainer>
                      </Col>
                    </StyledRow>
                  </Col>
                  <Col span={8}>
                    <Row gutter={4}>
                      <Col span={12}>
                        <StyledStats>
                          <Text className="label">Issues</Text>
                          <Text className="value">
                            {data?.issues?.length || 0}
                          </Text>
                          <Text>Report an issue</Text>
                        </StyledStats>
                      </Col>
                      <Col span={12}>
                        <StyledStats>
                          <Text className="label">Issues</Text>
                          <Text className="value">0</Text>
                          <Text>Report an issue</Text>
                        </StyledStats>
                      </Col>
                    </Row>
                  </Col>
                </>
              </StyledHeaderRow>
              <StyledHeaderRow $nav gutter={4}>
                <Col span={24}>
                  <Row gutter={[2, 2]} align="middle">
                    <Col xs={12}>
                      <CustomLink
                        href={ROUTES.JOBS_DETAIL}
                        as={`/jobs/${data?.id}`}
                      >
                        {router.pathname === ROUTES.JOBS_DETAIL ? (
                          <StyledNavLink $selected>Main</StyledNavLink>
                        ) : (
                          <StyledNavLink>Main</StyledNavLink>
                        )}
                      </CustomLink>

                      <CustomLink
                        href={ROUTES.JOBS_DETAIL_JOB_SHEETS}
                        as={`/jobs/${data?.id}/job-sheets`}
                      >
                        {router.pathname === ROUTES.JOBS_DETAIL_JOB_SHEETS ? (
                          <StyledNavLink $selected $ml>
                            Job Sheets
                          </StyledNavLink>
                        ) : (
                          <StyledNavLink $ml>Job Sheets</StyledNavLink>
                        )}
                      </CustomLink>
                    </Col>
                    <Col xs={12}>
                      <RightContainer>
                        {data.approval.order_by !== null && (
                          <Tooltip
                            title={
                              <span>
                                {moment(data.approval.order_at).format(
                                  "DD MMM YYYY h:mm A"
                                )}
                                , by: {data?.approval?.order_approved_by?.name}
                              </span>
                            }
                          >
                            <StyledApprovedContainer>
                              <CheckCircleTwoTone twoToneColor="#52c41a" />
                              <p>Order approved</p>
                            </StyledApprovedContainer>
                          </Tooltip>
                        )}

                        {data.approval.ctp_by !== null && (
                          <Tooltip
                            title={
                              <span>
                                {moment(data.approval.ctp_at).format(
                                  "DD MMM YYYY h:mm A"
                                )}
                                , by: {data?.approval?.ctp_approved_by?.name}
                              </span>
                            }
                          >
                            <StyledApprovedContainer
                              style={{ marginLeft: "10px" }}
                            >
                              <CheckCircleTwoTone twoToneColor="#52c41a" />
                              <p>Ctp approved</p>
                            </StyledApprovedContainer>
                          </Tooltip>
                        )}

                        {data && data.status === "new" && (
                          <CustomLink
                            href={ROUTES.JOBS_DETAIL_EDIT}
                            as={`/jobs/${data.id}/edit`}
                          >
                            <StyledEditButton
                              type="link"
                              icon={<EditOutlined />}
                            >
                              Edit this job
                            </StyledEditButton>
                          </CustomLink>
                        )}
                        {status !== "success" || status === "loading" ? (
                          <TagSkeleton />
                        ) : (
                          <Tag
                            style={{ marginLeft: "5px" }}
                            color={
                              data?.status === "new"
                                ? "blue"
                                : data?.status === "completed"
                                ? "#cbd5e0"
                                : data?.status === "stuck"
                                ? "red"
                                : data?.status === "started"
                                ? "green"
                                : "purple"
                            }
                          >
                            {data?.status}
                          </Tag>
                        )}
                      </RightContainer>
                    </Col>
                  </Row>
                </Col>
              </StyledHeaderRow>
            </>
          )}
        </>
      ) : (
        <>
          <StyledHeaderRow gutter={4} justify="middle">
            <JobInfoSkeleton />
          </StyledHeaderRow>
          <StyledHeaderRow $nav gutter={4}>
            <Col span={24}>
              <Row gutter={[2, 2]} align="middle">
                <Col span={16}>
                  <StyledNavLink $selected>Main</StyledNavLink>
                  <StyledNavLink $ml>Job Sheets</StyledNavLink>
                </Col>
                <Col span={8}>
                  <RightContainer>
                    <TagSkeleton />
                  </RightContainer>
                </Col>
              </Row>
            </Col>
          </StyledHeaderRow>
        </>
      )}
      <>
        {data?.status === "new" &&
          data?.approval?.order_by &&
          data?.approval?.ctp_by && (
            <ProgressContainer gutter={4}>
              <Col xs={24} align="">
                <GradientButton
                  loading={
                    notificationState.loading.updateJobStatus ? "true" : "false"
                  }
                  onClick={() => handleUpdateJobStatus("started")}
                >
                  <div>
                    {notificationState.loading.updateJobStatus ? (
                      <>
                        <LoadingOutlined
                          style={{ marginRight: "10px", color: "#718096" }}
                        />
                        <p>Starting this job!</p>
                      </>
                    ) : (
                      <p>Start this job!</p>
                    )}
                  </div>
                </GradientButton>
              </Col>
            </ProgressContainer>
          )}
      </>
      <>
        {data?.dateline?.length > 0 && (
          <ProgressContainer gutter={4}>
            <Col xs={24}>
              <div className="container">
                <p className="label">Job Completion</p>

                <Progress
                  percent={completionPercentage}
                  strokeWidth={12}
                  showInfo={false}
                />

                <p className="percentage">{completionPercentage} %</p>
              </div>
            </Col>
          </ProgressContainer>
        )}
      </>

      {props.children}
    </>
  );
};

const StyledText = styled(Text)`
  color: ${(props) => props.theme.colors.grey[700]};
  ${(props) =>
    props.$companyName &&
    css`
      &&& {
        font-size: 0.8rem;
        text-transform: uppercase;
        font-weight: 600;
      }
    `}
  ${(props) =>
    props.$createdAt &&
    css`
      font-weight: 500;
      font-size: 0.8rem;
    `}
    ${(props) =>
    props.$jobDateLabel &&
    css`
      &&& {
        color: ${(props) => props.theme.colors.grey[500]};
        text-transform: uppercase;
        font-size: 0.8rem;
        letter-spacing: 0.9px;
      }
    `}
    ${(props) =>
    props.urgent === "true" &&
    css`
      &&& {
        color: ${(props) => props.theme.colors.red[600]};
      }
    `};
`;

const StyledTitle = styled(Title)`
  ${(props) =>
    props.$jobName &&
    css`
      &&& {
        font-weight: 500;
        color: black;
        margin-top: 0;
        margin-bottom: 0;
      }
    `}

  ${(props) =>
    props.urgent === "true" &&
    css`
      &&& {
        color: ${(props) => props.theme.colors.red[800]};
      }
    `};
`;

const CreatedAtContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const DateContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledRow = styled(Row)`
  margin-top: ${(props) => props.theme.spacing[2]};
`;

const StyledStats = styled.div`
  padding: ${(props) => props.theme.spacing[3]};
  border: 1px solid ${(props) => props.theme.colors.grey[400]};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: white;
  height: 100%;
  .label {
    font-weight: 600;
    letter-spacing: 1px;
  }
  .value {
    font-weight: 700;
    font-size: 2.5rem;
  }
`;

const StyledHeaderRow = styled(Row)`
  background-color: ${(props) => props.theme.colors.grey[200]};
  padding: ${(props) => props.theme.spacing[3]};
  border: 1px solid ${(props) => props.theme.colors.grey[400]};
  border-bottom: none;
  ${(props) =>
    props.$nav &&
    css`
      background-color: ${(props) => props.theme.colors.grey[100]};
      border-top: none;
      padding: ${(props) => props.theme.spacing[2]}
        ${(props) => props.theme.spacing[3]};
      border-bottom: 1px solid ${(props) => props.theme.colors.grey[400]};
    `};

  ${(props) =>
    props.urgent === "true" &&
    css`
      background-color: ${(props) => props.theme.colors.red[100]};
    `};
`;

const StyledNavLink = styled(Text)`
  color: ${(props) => props.theme.colors.grey[600]};
  padding-bottom: ${(props) => props.theme.spacing[1]};
  :hover {
    border-bottom: 2px solid ${(props) => props.theme.colors.grey[400]};
  }
  ${(props) =>
    props.$selected &&
    css`
      font-weight: 600;
      color: ${(props) => props.theme.colors.primary};
      border-bottom: 2px solid ${(props) => props.theme.colors.primary};
      :hover {
        border-bottom: 2px solid ${(props) => props.theme.colors.primary};
      }
    `};
  ${(props) =>
    props.$ml &&
    css`
      margin-left: ${(props) => props.theme.spacing[3]};
    `};
`;

const RightContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const StyledNotifRow = styled(Row)`
  padding: ${(props) => props.theme.spacing[2]};
  border: 1px solid ${(props) => props.theme.colors.yellow[500]};
  color: ${(props) => props.theme.colors.yellow[800]};
  background-color: ${(props) => props.theme.colors.yellow[100]};
  margin-bottom: ${(props) => props.theme.spacing[2]};
  p {
    margin: 0;
  }
  .container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    .label {
      display: flex;
      align-items: baseline;
      span {
        font-style: italic;
        font-weight: 600;
        margin-bottom: 0;
        margin-left: ${(props) => props.theme.spacing[3]};
        font-size: ${(props) => props.theme.fontSize["xs"]};

        ::before {
          content: "at ";
        }
      }
    }
  }
`;

const StyledErrorMessage = styled.div`
  p {
    color: ${(props) => props.theme.colors.red[500]};
    font-weight: 600;
  }
`;

const ProgressContainer = styled(Row)`
  margin-top: ${(props) => props.theme.spacing[2]};
  background-color: white;
  padding: ${(props) => props.theme.spacing[2]};
  border: 1px solid ${(props) => props.theme.colors.grey[400]};

  .container {
    display: flex;
    align-items: center;
    .label {
      margin: 0;
      width: 140px;
      font-size: ${(props) => props.theme.fontSize["sm"]};
      letter-spacing: 0.2px;
      color: ${(props) => props.theme.colors.grey[600]};
      font-weight: 600;
    }
    .percentage {
      margin: 0;
      width: 70px;
      margin-left: ${(props) => props.theme.spacing[6]};
      font-size: ${(props) => props.theme.fontSize["sm"]};
      letter-spacing: 0.2px;
      color: ${(props) => props.theme.colors.grey[600]};
      font-weight: 600;
    }
  }
`;

const StyledEditButton = styled(Button)`
  color: ${(props) => props.theme.colors.blue[500]};

  :hover,
  :focus,
  :active {
    color: ${(props) => props.theme.colors.blue[600]};
    text-decoration: underline;
  }
`;

const StyledApprovedContainer = styled.div`
  display: flex;
  align-items: center;

  p {
    margin: 0;
    margin-left: ${(props) => props.theme.spacing[2]};
    color: ${(props) => props.theme.colors.green[500]};
    font-weight: 500;
  }
`;

const GradientButton = styled.a`
  position: relative;
  background: linear-gradient(
    -90deg,
    #ee7752,
    #e73c7e,
    #23a6d5,
    #23d5ab,
    #ee7752
  );
  background-size: 400% 100%;
  text-transform: uppercase;
  font-weight: 700;
  border: none;
  border-radius: 0.25em;
  font-size: 13px;
  letter-spacing: 1px;
  padding: 0;
  margin: 0;
  animation: Gradient 4s ease infinite;
  text-decoration: none;

  div {
    color: black;
    min-height: 40px;
    padding: 0 1.618em;
    border-radius: inherit;
    background-size: inherit;
    background-image: inherit;
    animation: Gradient 4s ease infinite;
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    p {
      mix-blend-mode: overlay;
      font-family: "Helvetica Neue";
      margin: 0;
      padding: 0;
    }
  }

  @keyframes Gradient {
    50% {
      background-position: 140% 50%;
      transform: skew(-2deg);
    }
  }

  ${(props) =>
    props.loading === "true" &&
    css`
      background: linear-gradient(
        -90deg,
        #cbd5e0,
        #a0aec0,
        #a0aec0,
        #a0aec0,
        #a0aec0
      );
      div {
        color: ${(props) => props.theme.colors.grey[900]};
      }
    `};

  ::after {
    content: "";
    position: absolute;
    background-size: inherit;
    background-image: inherit;
    animation: inherit;
    left: 0px;
    right: 0px;
    top: 2px;
    height: 100%;
    filter: blur(1rem);
  }
`;

export const getLayout = (page) =>
  getDefaultLayout(<JobLayout>{page}</JobLayout>);

export default JobLayout;
